<template>
  <span v-if="variant === 'inline'" class="bm-chargenote-inline" :data-testid="testid">
    <i class="material-icons bm-chargenote-inline__icon" aria-hidden="true">credit_card</i>
    Charged at release
  </span>
  <div v-else class="bm-chargenote" :data-testid="testid">
    <i class="material-icons bm-chargenote__icon" aria-hidden="true">credit_card</i>
    <div class="bm-chargenote__body">
      <b>Cards are charged when the batch is released.</b>
      Until then the saved card on each order is untouched.<template v-if="extra"> {{ extra }}</template>
    </div>
  </div>
</template>

<script>
/**
 * "Cards are charged when this batch is released."
 *
 * States the payment fact at THREE levels -- panel lede, batch detail, order
 * editor -- which is the shape ADR 0066 (decision 6) requires. The fact it
 * states is what changed, not the shape, which is why this component was
 * INVERTED for issue #3184 rather than deleted.
 *
 * HISTORY, because the two issues pull in opposite directions:
 *
 *   #2790 -- release charged NOTHING (`release_batch` ran with no `charge_fn`
 *   and fell through to `_charge_nothing`). A forward promise that "every card
 *   is charged the moment it is released" was therefore false and was removed,
 *   leaving "No card has been charged. Payment is not collected on this
 *   surface today."
 *
 *   #3184 -- charging is now WIRED, and that copy became the INVERSE lie.
 *   `batch_management.py` passes `charge_fn=make_charge_fn()` at three release
 *   call sites (:1170, :1428, :1604), `batch_auto_release.py:362` does the
 *   same, and `batch_charge.py` makes real off-session `PaymentIntent.create`
 *   calls. A component named `NotChargedNote` telling a distributor no payment
 *   is collected, on a screen whose button charges their shoppers' cards with
 *   NO retract path, is a worse failure than #2790 was: #2790 over-promised,
 *   this under-warned.
 *
 * THE FILE WAS RENAMED WITH THE FACT. A component called `NotChargedNote`
 * whose body says a card IS charged is a trap -- the next reader trusts the
 * name over the body, and the name is the thing that survives into every
 * import statement.
 *
 * WORDING CONSTRAINTS, inherited and load-bearing:
 *   - "the saved card on each order", never "your card". The distributor is
 *     NOT the payer; the shopper is. "Your card" tells a distributor they are
 *     about to be billed.
 *   - No amount. "Release and charge $631.00" was #2790's exact defect, and
 *     this surface still has no verified charge total to promise.
 *
 * Neutral, not alarming: this is a statement of when money moves, and it must
 * still leave editing legible as safe BEFORE release -- "if a distributor
 * doesn't believe editing is safe, they won't edit, and the batch window has
 * no value" (ADR 0066, decision 6). Hence the second clause: until release,
 * the card is untouched.
 */
export default {
  name: "ChargeAtReleaseNote",
  props: {
    variant: {
      type: String,
      default: "block",
      validator: (v) => ["block", "inline"].includes(v),
    },
    extra: { type: String, default: "" },
    testid: { type: String, default: undefined },
  },
};
</script>

<style scoped>
/*
 * Still green, deliberately, and #3184 did NOT change it.
 *
 * The note is rendered only on Open and Holding batches (BatchRow gates it on
 * `editable`), where its message remains reassurance: the card has not been
 * touched yet and editing is still safe. The WARNING about the charge belongs
 * on the confirm step, where the irreversible click is -- ReleaseReview's
 * amber no-undo block and its confirm note carry it.
 *
 * Colouring this amber would put a caution on every open batch a distributor
 * scrolls past, which trains the caution away exactly where it matters.
 */
.bm-chargenote {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: var(--tf-success-soft);
  border: 1px solid var(--tf-chip-success-border);
  border-radius: 10px;
  padding: 11px 14px;
}

.bm-chargenote__icon {
  font-size: 18px;
  margin-top: 1px;
  color: var(--tf-success-dark);
  flex-shrink: 0;
}

.bm-chargenote__body {
  font-size: 13px;
  color: var(--tf-success-dark);
  line-height: 1.5;
}

.bm-chargenote__body b {
  font-weight: 500;
}

.bm-chargenote-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--tf-success-dark);
}

.bm-chargenote-inline__icon {
  font-size: 15px;
}
</style>
